import { LOCALES, type Locale } from './alphabet';
import { type Dir, cellToIndex, stepIndex } from './grid';

/**
 * 게임 규칙 상수 — 봉인 구조(SEAL.md §2). RULES.md 가 단일 출처이고 이 객체는 그 번역이다.
 *
 * 길이 경제(핵심):
 *   시간이 지나면 자란다(growPeriodTable, 이동 틱 기준) · 정답 글자 −1 · 완성 −2(무오답 −3) · 오답 +1
 *   논리 길이 = cells.length + pending. 하한 3(잉여 폐기) · 상한 40 도달 = engorged 판 종료
 *   점수·콤보·타임아웃·하한 오답 예외는 전부 폐지됐다 — 부호가 뒤집히면 하한은 치트가 아니라 목표다.
 */
export interface EngineRules {
  readonly gridCols: number;
  readonly gridRows: number;
  readonly runWordCount: number;
  readonly wordLenProfile: readonly number[];
  readonly tickMsTable: readonly number[];
  readonly nominalTickMs: number; // = round(mean(tickMsTable)). 명목 시간 = tick × 이 값
  readonly penaltyTickFloorMs: number;
  readonly readyTicks: number;
  readonly wordClearTicks: number;
  readonly transitionGraceTicks: number;
  readonly runMaxTicks: number;
  readonly snakeLenInit: number;
  readonly snakeLenMin: number;
  readonly snakeLenMax: number; // 논리 길이 ≥ 이 값 → engorged
  readonly snakeStartCell: number;
  readonly snakeStartDir: Dir;
  readonly shrinkOnCorrect: number; // 정답 글자 1개 = −1
  readonly shrinkOnWord: number; // 단어 완성 = −2
  readonly shrinkOnPerfectWord: number; // 무오답 완성 추가 −1
  readonly growOnWrong: number; // 오답 = +1 (논리 길이 ≤ snakeLenMax−1 클램프)
  readonly growPeriodTable: readonly number[]; // 문제 인덱스별 자동 성장 주기 (이동 틱)
  readonly growPauseOnWord: number; // 해결 시 period[해결 인덱스] × 이 값 이동 틱 동안 성장 정지
  readonly penaltyTicks: number;
  readonly penaltySpeedDivisor: number;
  readonly penaltyArmMinCells: number;
  readonly penaltyAbortCells: number;
  readonly penaltyDeferMaxTicks: number;
  readonly distractorCount: number;
  readonly spawnMargin: number;
  readonly minTileGap: number;
  readonly tileHeadMinDist: number;
  readonly tileHeadMaxDist: number;
  readonly answerBandCenterMin: number;
  readonly answerBandCenterMax: number;
  readonly answerBandSpread: number;
  readonly spawnSafeAhead: number;
  readonly spawnAttemptsPerLevel: number;
  readonly decoyOn: boolean;
  readonly decoyMinDist: number;
  readonly decoyMaxDist: number;
  readonly relocateTicks: number;
  readonly relocateSealedConfirmTicks: number;
  readonly relocateMaxPerWord: number;
  readonly ladderWindowFactor: number;
  readonly hintEnabled: boolean; // 분노 게이지 힌트(RULES §1.16). 타일 하이라이트가 아니다
  readonly angerStageTicks: readonly number[]; // wordTick 임계 — 오름차순
  readonly angerSpeedStepPct: number; // 단계당 실효 틱 간격 ÷ (1 + pct/100 × 단계)
  // 부활 (RULES §1.14 · ADR-032) — 전부 engineRulesHash 에 포함
  readonly reviveEnabled: boolean;
  readonly reviveMaxOffers: number; // 판당 제시 상한 (1 · P4 보상형이 2)
  readonly reviveChoiceCount: number; // 4지선다
  readonly reviveTickMs: number; // 부활 페이즈 전용 실효 틱
  readonly reviveArmTicks: number; // 오조작 방지 — 제한 시간 미소모
  readonly reviveLimitTicks: number; // 선택 제한
  readonly reviveRevealTicks: number; // 정답 공개
  readonly reviveReadyTicks: number; // 부활 후 정지
}

export interface ViewRules {
  readonly dirQueueMax: number;
  readonly dirPerTick: number;
  readonly swipeMinDistPx: number;
  readonly swipeWindowMs: number;
  readonly swipeAxisRatio: number;
  readonly edgeDeadzonePx: number;
  readonly maxFrameDeltaMs: number;
  readonly maxCatchupTicks: number;
  readonly gameoverInputLockMs: number;
  readonly pauseAbandonMs: number;
  readonly cellCssMin: number;
  readonly cellCssMax: number;
  readonly bandMinCss: number;
  readonly bandMaxCss: number;
  readonly bandFloorCss: number;
  readonly hudMinCss: number;
  readonly hudMaxCss: number;
  readonly hudFloorCss: number;
  readonly slackToBand: number;
  readonly slackToHud: number;
  readonly marginTopRatio: number;
  readonly inAppBottomGuardCss: number;
  readonly relayoutDebounceMs: number;
  readonly tileFill: number;
  readonly fontLoadTimeoutMs: number;
  readonly renderInterpolate: boolean;
  readonly alphaSteps: number;
  readonly alphaStepsPenalty: number;
  readonly boostEnabled: boolean;
}

export type RuleScope = 'engine' | 'view';
export type RuleMeta =
  | {
      readonly scope: RuleScope;
      readonly kind: 'int' | 'float';
      readonly label: string;
      readonly min: number;
      readonly max: number;
      readonly step: number;
      readonly unit?: string;
    }
  | { readonly scope: RuleScope; readonly kind: 'bool'; readonly label: string }
  | { readonly scope: RuleScope; readonly kind: 'fixed'; readonly label: string };

export type RuleKey = keyof EngineRules | keyof ViewRules;

const e = (
  kind: 'int' | 'float',
  label: string,
  min: number,
  max: number,
  step: number,
  unit?: string,
): RuleMeta =>
  unit === undefined
    ? { scope: 'engine', kind, label, min, max, step }
    : { scope: 'engine', kind, label, min, max, step, unit };
const v = (
  kind: 'int' | 'float',
  label: string,
  min: number,
  max: number,
  step: number,
  unit?: string,
): RuleMeta =>
  unit === undefined
    ? { scope: 'view', kind, label, min, max, step }
    : { scope: 'view', kind, label, min, max, step, unit };
const fixedE = (label: string): RuleMeta => ({ scope: 'engine', kind: 'fixed', label });
const fixedV = (label: string): RuleMeta => ({ scope: 'view', kind: 'fixed', label });

/** 룰을 추가하고 메타를 빠뜨리면 컴파일 에러가 난다. kind 'fixed' 는 URL 오버라이드 대상이 아니다. */
export const RULES_META: Readonly<Record<RuleKey, RuleMeta>> = {
  gridCols: fixedE('그리드 열'),
  gridRows: fixedE('그리드 행'),
  runWordCount: fixedE('판당 문제 수'),
  wordLenProfile: fixedE('문제별 정답 길이'),
  tickMsTable: e('int', '틱 간격 테이블', 45, 400, 1, 'ms'),
  nominalTickMs: fixedE('명목 틱 ms (파생)'),
  penaltyTickFloorMs: e('int', '페널티 틱 하한', 45, 180, 5, 'ms'),
  readyTicks: e('int', 'READY 틱', 0, 30, 1),
  wordClearTicks: e('int', '단어 완성 정지 틱', 0, 20, 1),
  transitionGraceTicks: e('int', '전환 유예 틱', 0, 8, 1),
  runMaxTicks: fixedE('판 최대 틱'),
  snakeLenInit: fixedE('초기 길이'),
  snakeLenMin: fixedE('길이 하한'),
  snakeLenMax: e('int', '길이 상한 (engorged)', 20, 42, 1),
  snakeStartCell: fixedE('시작 셀'),
  snakeStartDir: fixedE('시작 방향'),
  shrinkOnCorrect: fixedE('정답 수축'),
  shrinkOnWord: fixedE('완성 수축'),
  shrinkOnPerfectWord: fixedE('무오답 완성 추가 수축'),
  growOnWrong: fixedE('오답 성장'),
  growPeriodTable: e('int', '자동 성장 주기 테이블', 3, 400, 1, 'ticks'),
  growPauseOnWord: e('int', '해결 시 성장 정지 (주기 배수)', 0, 6, 1),
  penaltyTicks: e('int', '페널티 지속', 0, 30, 1),
  penaltySpeedDivisor: e('int', '페널티 속도 배수', 1, 4, 1),
  penaltyArmMinCells: e('int', '페널티 발동 최소 잔여칸', 1, 10, 1),
  penaltyAbortCells: e('int', '페널티 해제 잔여칸', 0, 6, 1),
  penaltyDeferMaxTicks: e('int', '페널티 유예 상한', 0, 30, 1),
  distractorCount: e('int', '오답 타일 수', 4, 12, 1),
  spawnMargin: e('int', '스폰 여백', 0, 3, 1),
  minTileGap: e('int', '타일 최소 간격', 1, 4, 1),
  tileHeadMinDist: e('int', '머리 최소 거리', 2, 10, 1),
  tileHeadMaxDist: e('int', '머리 최대 거리', 6, 27, 1),
  answerBandCenterMin: e('int', '정답 밴드 하한', 3, 12, 1),
  answerBandCenterMax: e('int', '정답 밴드 상한', 4, 20, 1),
  answerBandSpread: e('int', '정답 밴드 폭', 1, 8, 1),
  spawnSafeAhead: fixedE('머리 앞 안전 칸'),
  spawnAttemptsPerLevel: e('int', '레벨당 배치 시도', 1, 32, 1),
  decoyOn: fixedE('오답 타일 사용'),
  decoyMinDist: e('int', '근접 함정 최소', 2, 8, 1),
  decoyMaxDist: e('int', '근접 함정 최대', 3, 12, 1),
  relocateTicks: e('int', '진전 없음 재배치 틱', 10, 200, 5),
  relocateSealedConfirmTicks: e('int', '봉인 확인 틱', 1, 10, 1),
  relocateMaxPerWord: e('int', '문제당 재배치 상한', 0, 8, 1),
  ladderWindowFactor: e('int', '사다리 창 배수', 1, 8, 1),
  hintEnabled: fixedE('분노 게이지 힌트'),
  angerStageTicks: e('int', '분노 단계 틱 테이블', 1, 2000, 1, 'ticks'),
  angerSpeedStepPct: e('int', '분노 단계당 가속 %', 0, 30, 1),
  reviveEnabled: { scope: 'engine', kind: 'bool', label: '부활' },
  reviveMaxOffers: e('int', '부활 제시 상한', 0, 2, 1),
  reviveChoiceCount: fixedE('부활 선택지 수'),
  reviveTickMs: e('int', '부활 틱', 50, 200, 5, 'ms'),
  reviveArmTicks: e('int', '부활 오조작 방지 틱', 0, 20, 1),
  reviveLimitTicks: e('int', '부활 제한 틱', 30, 300, 5),
  reviveRevealTicks: e('int', '부활 정답 공개 틱', 0, 30, 1),
  reviveReadyTicks: e('int', '부활 후 정지 틱', 0, 20, 1),

  dirQueueMax: v('int', '방향 큐 깊이', 1, 3, 1),
  dirPerTick: fixedV('틱당 방향 소비'),
  swipeMinDistPx: v('int', '스와이프 최소 거리', 8, 64, 2, 'px'),
  swipeWindowMs: v('int', '스와이프 시간창', 80, 600, 10, 'ms'),
  swipeAxisRatio: v('float', '주축 우세 비율', 1, 3, 0.1),
  edgeDeadzonePx: v('int', '가장자리 데드존', 0, 48, 2, 'px'),
  maxFrameDeltaMs: fixedV('프레임 델타 상한'),
  maxCatchupTicks: fixedV('캐치업 상한'),
  gameoverInputLockMs: v('int', '게임오버 입력 잠금', 0, 2000, 50, 'ms'),
  pauseAbandonMs: v('int', '이탈 판정 시간', 60000, 7200000, 60000, 'ms'),
  cellCssMin: v('int', '셀 최소', 16, 40, 1, 'px'),
  cellCssMax: v('int', '셀 최대', 24, 64, 1, 'px'),
  bandMinCss: v('int', '문제 띠 최소', 40, 200, 2, 'px'),
  bandMaxCss: v('int', '문제 띠 최대', 60, 200, 2, 'px'),
  bandFloorCss: v('int', '문제 띠 하한', 40, 200, 2, 'px'),
  hudMinCss: v('int', 'HUD 최소', 20, 96, 2, 'px'),
  hudMaxCss: v('int', 'HUD 최대', 24, 96, 2, 'px'),
  hudFloorCss: v('int', 'HUD 하한', 20, 96, 2, 'px'),
  slackToBand: v('float', '여유→띠 배분', 0, 1, 0.05),
  slackToHud: v('float', '여유→HUD 배분', 0, 1, 0.05),
  marginTopRatio: v('float', '상단 여백 비율', 0, 1, 0.05),
  inAppBottomGuardCss: v('int', '인앱 하단 가드', 0, 96, 2, 'px'),
  relayoutDebounceMs: v('int', '리레이아웃 디바운스', 0, 600, 10, 'ms'),
  tileFill: v('float', '타일 채움 비율', 0.5, 1, 0.01),
  fontLoadTimeoutMs: v('int', '폰트 로드 타임아웃', 200, 5000, 100, 'ms'),
  renderInterpolate: fixedV('이동 보간'),
  alphaSteps: v('int', '보간 단계', 1, 8, 1),
  alphaStepsPenalty: v('int', '페널티 보간 단계', 1, 8, 1),
  boostEnabled: fixedV('가속 입력(P1)'),
};

/** 정답 타일 쌍 사이 최소 맨해튼 거리 (정답 길이별). SPEC §7.1 */
export const ANSWER_PAIR_MIN_DIST: Readonly<Record<number, number>> = { 2: 8, 3: 6, 4: 5, 5: 4 };

/** 오답 풀 하한 = 최대 distractorCount. SPEC §8.4 */
export const DISTRACTOR_POOL_MIN = 12;

/** 결정론 회로(마진1 직사각, 둘레 46)가 살아남는 상한. snakeLenMax + 4 <= 이 값 */
export const CIRCUIT_PERIMETER = 46;

const TICK_MS_TABLE = [180, 167, 154, 141, 129, 116, 103, 90] as const;

export const DEFAULT_ENGINE_RULES: EngineRules = {
  gridCols: 11,
  gridRows: 18,
  runWordCount: 8,
  wordLenProfile: [2, 2, 3, 3, 3, 3, 4, 4],
  tickMsTable: [...TICK_MS_TABLE],
  nominalTickMs: 135, // = 1080 / 8
  penaltyTickFloorMs: 60,
  readyTicks: 11,
  wordClearTicks: 6,
  transitionGraceTicks: 2,
  runMaxTicks: 1200,
  snakeLenInit: 5,
  snakeLenMin: 3,
  snakeLenMax: 40,
  snakeStartCell: cellToIndex(5, 12),
  snakeStartDir: 0,
  shrinkOnCorrect: 1,
  shrinkOnWord: 2,
  shrinkOnPerfectWord: 1,
  growOnWrong: 1,
  growPeriodTable: [12, 11, 10, 9, 8, 7, 7, 6],
  growPauseOnWord: 2,
  penaltyTicks: 8,
  penaltySpeedDivisor: 2,
  penaltyArmMinCells: 4,
  penaltyAbortCells: 2,
  penaltyDeferMaxTicks: 8,
  distractorCount: 8,
  spawnMargin: 1,
  minTileGap: 2,
  tileHeadMinDist: 4,
  tileHeadMaxDist: 16,
  answerBandCenterMin: 5,
  answerBandCenterMax: 9,
  answerBandSpread: 3,
  spawnSafeAhead: 3,
  spawnAttemptsPerLevel: 8,
  decoyOn: true,
  decoyMinDist: 3,
  decoyMaxDist: 6,
  relocateTicks: 40,
  relocateSealedConfirmTicks: 3,
  relocateMaxPerWord: 3,
  ladderWindowFactor: 3,
  hintEnabled: true,
  angerStageTicks: [72, 108, 144, 180],
  angerSpeedStepPct: 10,
  reviveEnabled: true,
  reviveMaxOffers: 1,
  reviveChoiceCount: 4,
  reviveTickMs: 100,
  reviveArmTicks: 4,
  reviveLimitTicks: 100,
  reviveRevealTicks: 9,
  reviveReadyTicks: 6,
};

/** 부활 페이즈가 최대로 먹는 틱 — arm + 제한 + 공개 + 정지. 판 상한 검산과 판정 도구가 쓴다. */
export function reviveMaxTicks(r: EngineRules): number {
  return (
    r.reviveMaxOffers *
    (r.reviveArmTicks + r.reviveLimitTicks + r.reviveRevealTicks + r.reviveReadyTicks)
  );
}

/**
 * 테스트 전용 변종 — 성장을 사실상 끄고 상한을 회로 한계까지 올려
 * determinism·frame-independence 가 8문제 전 구간을 계속 덮게 한다(SEAL §5.G).
 */
export const TEST_LONG_RULES: EngineRules = {
  ...DEFAULT_ENGINE_RULES,
  growPeriodTable: [400, 400, 400, 400, 400, 400, 400, 400],
  // 회로 파일럿은 링 위의 오답을 40개 넘게 먹어 오답 성장만으로 41 에 포화한다 — 성장을 '사실상 끈다'(SEAL §5.G)는
  // 자동 성장뿐 아니라 오답 성장도 포함해야 8문제 전 구간(READY + WORD_CLEAR 8회)을 덮는다.
  growOnWrong: 0,
  snakeLenMax: 42, // + 4 ≤ CIRCUIT_PERIMETER 46
  runMaxTicks: 2000,
};

/** 판 불변식 — 발행되는 총 수축량(흡수량이 아니다). SEAL §2.2 */
export function sealTotalShrink(r: EngineRules): number {
  let n = 0;
  for (const L of r.wordLenProfile) n += L * r.shrinkOnCorrect;
  return n + r.runWordCount * (r.shrinkOnWord + r.shrinkOnPerfectWord);
}

/**
 * 로케일 규칙 — 팩 로케일의 정답 길이 프로필을 덮어쓴다(RULES §1.17). ko 는 기본값 그대로라 engineRulesHash 가 변하지 않는다.
 * URL 오버라이드가 적용된 규칙 위에 얹어도 된다(프로필은 fixed 키라 URL 로는 못 바꾼다).
 */
export function rulesForLocale(
  locale: Locale,
  base: EngineRules = DEFAULT_ENGINE_RULES,
): EngineRules {
  const profile = LOCALES[locale].wordLenProfile;
  const same =
    base.wordLenProfile.length === profile.length &&
    base.wordLenProfile.every((L, i) => L === profile[i]);
  return same ? base : { ...base, wordLenProfile: [...profile] };
}

/** 부팅 어서션. 룰 하나가 흔들리면 아래 전부가 흔들리므로 진입부에서 터뜨린다. */
export function assertEngineRules(r: EngineRules): void {
  const fail = (m: string): never => {
    throw new Error(`RULES 위반: ${m}`);
  };
  if (r.tickMsTable.length !== r.runWordCount) fail('tickMsTable.length !== runWordCount');
  if (r.wordLenProfile.length !== r.runWordCount) fail('wordLenProfile.length !== runWordCount');
  if (r.growPeriodTable.length !== r.runWordCount) fail('growPeriodTable.length !== runWordCount');
  if (!(r.snakeLenMin < r.snakeLenInit && r.snakeLenInit < r.snakeLenMax)) {
    fail('snakeLenMin < snakeLenInit < snakeLenMax 위반');
  }
  if (r.snakeLenMax + 4 > CIRCUIT_PERIMETER) fail('snakeLenMax + 4 > 회로 둘레 46');
  let sum = 0;
  for (const ms of r.tickMsTable) {
    if (!Number.isInteger(ms) || ms < 45) fail('tickMsTable 원소는 45 이상 정수');
    sum += ms;
  }
  if (r.nominalTickMs !== Math.round(sum / r.tickMsTable.length)) {
    fail('nominalTickMs !== round(mean(tickMsTable))');
  }
  for (const p of r.growPeriodTable)
    if (!Number.isInteger(p) || p < 1) fail('growPeriodTable 원소는 1 이상 정수');
  if (r.answerBandCenterMin > r.answerBandCenterMax)
    fail('answerBandCenterMin > answerBandCenterMax');
  if (r.decoyMinDist > r.decoyMaxDist) fail('decoyMinDist > decoyMaxDist');
  if (r.tileHeadMinDist > r.tileHeadMaxDist) fail('tileHeadMinDist > tileHeadMaxDist');
  if (r.distractorCount > DISTRACTOR_POOL_MIN) fail('distractorCount > DISTRACTOR_POOL_MIN');
  for (const L of r.wordLenProfile) {
    if (ANSWER_PAIR_MIN_DIST[L] === undefined) fail(`ANSWER_PAIR_MIN_DIST 에 길이 ${L} 없음`);
  }
  if (sealTotalShrink(r) < 40) fail('발행 수축량 < 40 — 봉인이 성립하지 않는다');
  if (r.reviveChoiceCount < 2) fail('reviveChoiceCount < 2');
  if (!Number.isInteger(r.reviveMaxOffers) || r.reviveMaxOffers < 0 || r.reviveMaxOffers > 2)
    fail('reviveMaxOffers 는 0..2');
  for (const k of [
    'reviveTickMs',
    'reviveArmTicks',
    'reviveLimitTicks',
    'reviveRevealTicks',
    'reviveReadyTicks',
  ] as const) {
    if (!Number.isInteger(r[k]) || r[k] < 0) fail(`${k} 는 0 이상 정수`);
  }
  if (r.reviveTickMs < 45) fail('reviveTickMs < 45');
  let prevStage = 0;
  for (const t of r.angerStageTicks) {
    if (!Number.isInteger(t) || t <= prevStage) fail('angerStageTicks 는 1 이상 오름차순 정수');
    prevStage = t;
  }
  if (!Number.isInteger(r.angerSpeedStepPct) || r.angerSpeedStepPct < 0 || r.angerSpeedStepPct > 30)
    fail('angerSpeedStepPct 는 0..30');
  // 시작 직후 자기 몸 충돌 불가 — 진행 방향 앞칸이 벽·몸통이 아니다
  const ahead = stepIndex(r.snakeStartCell, r.snakeStartDir);
  if (ahead < 0) fail('시작 위치 앞이 벽');
  const back = ((r.snakeStartDir + 2) & 3) as Dir;
  let i = r.snakeStartCell;
  for (let n = 1; n < r.snakeLenInit; n++) {
    i = stepIndex(i, back);
    if (i < 0) fail('시작 몸통이 벽을 넘는다');
    if (i === ahead) fail('시작 몸통이 진행 방향 앞칸을 막는다');
  }
}
